/**
 * Chat store - lightweight conversation store for the chat panel.
 * Non-persisted; cleared on each new voice session.
 */

import { create } from "zustand";
import { nanoid } from "nanoid";

export interface ChatMessage {
  id: string;
  role: "ai" | "user";
  source: "voice" | "text";
  content: string;
  timestamp: number;
}

/** Window (ms) within which identical user role+content messages are treated as duplicates. */
const DEDUP_WINDOW_MS = 60000;

/** Collapse whitespace for dedup comparison (trims + normalizes internal runs). */
function normalizeForDedup(s: string): string {
  return s.trim().replace(/\s+/g, " ");
}

/**
 * Check if the same role+content message already exists in recent history.
 *
 * AI messages dedup against the LAST ai message regardless of time — Sonic
 * frequently re-emits the trailing assistant text on session auto-restart
 * (Bedrock conversation history echo) and it almost never repeats itself
 * verbatim for legitimate reasons.
 *
 * User messages still dedup within a rolling time window so a kid saying the
 * same thing on purpose minutes later is preserved.
 */
function isDuplicateRecent(messages: ChatMessage[], role: string, content: string): boolean {
  const normalized = normalizeForDedup(content);
  if (role === "ai") {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role !== "ai") continue;
      return normalizeForDedup(messages[i].content) === normalized;
    }
    return false;
  }
  const cutoff = Date.now() - DEDUP_WINDOW_MS;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.timestamp < cutoff) break; // older than window, stop scanning
    if (m.role === role && normalizeForDedup(m.content) === normalized) return true;
  }
  return false;
}

interface ChatState {
  messages: ChatMessage[];
  isOpen: boolean;
  currentAiBuffer: string;
  focusRequested: boolean;

  addMessage: (msg: Omit<ChatMessage, "id" | "timestamp">) => void;
  startAiTurn: () => void;
  appendAiChunk: (text: string) => void;
  finalizeAiTurn: () => void;
  clearMessages: () => void;
  toggleOpen: () => void;
  setOpen: (open: boolean) => void;
  consumeFocus: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  isOpen: false,
  currentAiBuffer: "",
  focusRequested: false,

  addMessage: (msg) => {
    const { messages } = get();
    // Skip if an identical message was added within the last few seconds
    // (e.g. Bedrock history echo replayed on session restart).
    if (isDuplicateRecent(messages, msg.role, msg.content)) return;
    set((s) => ({
      messages: [
        ...s.messages,
        { ...msg, id: nanoid(), timestamp: Date.now() },
      ],
    }));
  },

  startAiTurn: () => set({ currentAiBuffer: "" }),

  appendAiChunk: (text) =>
    set((s) => ({ currentAiBuffer: s.currentAiBuffer + text })),

  finalizeAiTurn: () => {
    const { currentAiBuffer, messages } = get();
    const content = currentAiBuffer.trim();
    if (!content) {
      if (currentAiBuffer) set({ currentAiBuffer: "" });
      return;
    }
    // Skip if an identical AI message was added within the last few seconds
    // (e.g. Bedrock sends duplicate TEXT blocks within the same turn).
    if (isDuplicateRecent(messages, "ai", content)) {
      set({ currentAiBuffer: "" });
      return;
    }
    set((s) => ({
      messages: [
        ...s.messages,
        {
          id: nanoid(),
          role: "ai",
          source: "voice",
          content,
          timestamp: Date.now(),
        },
      ],
      currentAiBuffer: "",
    }));
  },

  clearMessages: () => set({ messages: [], currentAiBuffer: "" }),

  toggleOpen: () =>
    set((s) => {
      const willOpen = !s.isOpen;
      return { isOpen: willOpen, focusRequested: willOpen };
    }),

  setOpen: (open) => set({ isOpen: open, focusRequested: open }),

  consumeFocus: () => set({ focusRequested: false }),
}));
